// meshes.bin パーサ（SPEC_FMAVATAR.md §3）。
//
// レイアウト:
//   FileHeader 16B: magic "FMSH" / formatVersion u32 / meshCount u32 / reserved u32
//   MeshTable  meshCount × 8B: offset u32, length u32
//   MeshBlock × meshCount:
//     MeshBlockHeader 56B: magic "FMSM"(4) / meshIndex(4) / vertexCount(4) / submeshCount(4) /
//                          blendshapeCount(4) / totalIndexCount(4) / indexFormat u8(1) / reserved[3](3) /
//                          transform.position f32[3](12) / transform.rotation f32[4](16)
//
// ※ 仕様書 SPEC_FMAVATAR.md §3.3 は見出しに「48 bytes」と書いているが、
//    列挙されているフィールドのサイズ合計は 56 バイトである（4+4*5+1+3+12+16）。
//    本実装はフィールド列挙のほうを正とし 56 バイトとして扱う。
//    Unity 側実装と突き合わせる際はここを最優先で確認すること。
//     Vertices  vertexCount × 48B (pos12 + normal12 + tangent16 + uv8)
//     SubMeshes submeshCount × 8B (indexOffset, indexCount)
//     Indices   totalIndexCount × (2|4)B
//     Blendshapes: per shape { sparseCount u32, entries × 28B }
//
// リトルエンディアン固定。

import {
  LIMITS,
  MESHBIN_FORMAT_VERSION,
  type MeshBinBlendshape,
  type MeshBinSubMesh,
  type ParsedMeshBlock,
  type ParsedMeshesBin,
} from "./types.ts";
import { fail } from "./errors.ts";

const WHERE = "meshes.bin";

export const FILE_HEADER_SIZE = 16;
export const MESH_TABLE_ENTRY_SIZE = 8;
/** 仕様のフィールド列挙どおりの実サイズ（見出しの「48」は誤記とみなす。冒頭コメント参照） */
export const MESH_BLOCK_HEADER_SIZE = 56;
export const VERTEX_STRIDE = 48;
export const SUBMESH_STRIDE = 8;
export const DELTA_STRIDE = 28;

function readMagic(view: DataView, off: number): string {
  return String.fromCharCode(
    view.getUint8(off),
    view.getUint8(off + 1),
    view.getUint8(off + 2),
    view.getUint8(off + 3),
  );
}

function checkFinite(v: number, ctx: string): number {
  if (!Number.isFinite(v)) {
    fail(WHERE, `${ctx} に NaN または Infinity が含まれています。`, "Unity 側のエクスポート結果が壊れている可能性があります。再エクスポートしてください。");
  }
  return v;
}

/** meshes.bin 全体をパースする。 */
export function parseMeshesBin(buffer: ArrayBuffer): ParsedMeshesBin {
  if (buffer.byteLength < FILE_HEADER_SIZE) {
    fail(WHERE, `ファイルが小さすぎます（${buffer.byteLength} バイト、ヘッダに ${FILE_HEADER_SIZE} バイト必要）。`);
  }
  const view = new DataView(buffer);

  const magic = readMagic(view, 0);
  if (magic !== "FMSH") {
    fail(WHERE, `先頭のマジックが "FMSH" ではありません（実際: "${magic}"）。`, ".fmavatar ファイルが壊れているか、別形式のファイルです。");
  }
  const formatVersion = view.getUint32(4, true);
  if (formatVersion !== MESHBIN_FORMAT_VERSION) {
    fail(WHERE, `meshes.bin の formatVersion が ${MESHBIN_FORMAT_VERSION} ではありません（実際: ${formatVersion}）。`, "FaceMaker（Web / Unity 拡張）のバージョンを揃えてください。");
  }
  const meshCount = view.getUint32(8, true);
  if (meshCount === 0) {
    fail(WHERE, "メッシュが 1 つも含まれていません。", "Unity 側でシェイプキーを持つメッシュが検出されているか確認してください。");
  }
  if (meshCount > LIMITS.meshCount) {
    fail(WHERE, `meshCount(${meshCount}) が上限 ${LIMITS.meshCount} を超えています。`);
  }

  const tableSize = meshCount * MESH_TABLE_ENTRY_SIZE;
  if (buffer.byteLength < FILE_HEADER_SIZE + tableSize) {
    fail(WHERE, `メッシュテーブル（${tableSize} バイト）がファイル末尾を超えています。`);
  }

  const blocks: ParsedMeshBlock[] = [];
  const seenIndices = new Set<number>();

  for (let i = 0; i < meshCount; i++) {
    const entryOff = FILE_HEADER_SIZE + i * MESH_TABLE_ENTRY_SIZE;
    const offset = view.getUint32(entryOff, true);
    const length = view.getUint32(entryOff + 4, true);
    if (offset + length > buffer.byteLength) {
      fail(WHERE, `メッシュ ${i} のブロック範囲（offset=${offset}, length=${length}）がファイルサイズ ${buffer.byteLength} を超えています。`);
    }
    if (offset < FILE_HEADER_SIZE + tableSize) {
      fail(WHERE, `メッシュ ${i} のブロック offset(${offset}) がヘッダ領域と重なっています。`);
    }
    const block = parseMeshBlock(buffer, offset, length, i);
    if (seenIndices.has(block.meshIndex)) {
      fail(WHERE, `meshIndex ${block.meshIndex} が重複しています。`);
    }
    seenIndices.add(block.meshIndex);
    blocks.push(block);
  }

  return { formatVersion, meshCount, blocks };
}

/** MeshBlock 1 個分をパースする（offset は buffer 先頭からの絶対位置）。 */
export function parseMeshBlock(
  buffer: ArrayBuffer,
  blockOffset: number,
  blockLength: number,
  tableIndex: number,
): ParsedMeshBlock {
  const label = `メッシュ ${tableIndex}`;
  if (blockLength < MESH_BLOCK_HEADER_SIZE) {
    fail(WHERE, `${label}: ブロック長(${blockLength})がヘッダ ${MESH_BLOCK_HEADER_SIZE} バイト未満です。`);
  }
  const view = new DataView(buffer, blockOffset, blockLength);

  const magic = readMagic(view, 0);
  if (magic !== "FMSM") {
    fail(WHERE, `${label}: ブロックのマジックが "FMSM" ではありません（実際: "${magic}"）。`);
  }
  const meshIndex = view.getUint32(4, true);
  const vertexCount = view.getUint32(8, true);
  const submeshCount = view.getUint32(12, true);
  const blendshapeCount = view.getUint32(16, true);
  const totalIndexCount = view.getUint32(20, true);
  const indexFormatRaw = view.getUint8(24);
  if (indexFormatRaw !== 0x00 && indexFormatRaw !== 0x01) {
    fail(WHERE, `${label}: indexFormat が 0x00/0x01 以外です（0x${indexFormatRaw.toString(16)}）。`);
  }
  const indexFormat: 0 | 1 = indexFormatRaw === 0x01 ? 1 : 0;

  // transform: position f32[3] @28, rotation f32[4] @40
  const transform = {
    position: [
      checkFinite(view.getFloat32(28, true), `${label} transform.position`),
      checkFinite(view.getFloat32(32, true), `${label} transform.position`),
      checkFinite(view.getFloat32(36, true), `${label} transform.position`),
    ] as [number, number, number],
    rotation: [
      checkFinite(view.getFloat32(40, true), `${label} transform.rotation`),
      checkFinite(view.getFloat32(44, true), `${label} transform.rotation`),
      checkFinite(view.getFloat32(48, true), `${label} transform.rotation`),
      checkFinite(view.getFloat32(52, true), `${label} transform.rotation`),
    ] as [number, number, number, number],
  };

  if (vertexCount > LIMITS.vertexCount) {
    fail(WHERE, `${label}: vertexCount(${vertexCount}) が上限 ${LIMITS.vertexCount} を超えています。`);
  }
  if (totalIndexCount > LIMITS.totalIndexCount) {
    fail(WHERE, `${label}: totalIndexCount(${totalIndexCount}) が上限 ${LIMITS.totalIndexCount} を超えています。`);
  }
  if (blendshapeCount > LIMITS.blendshapeCount) {
    fail(WHERE, `${label}: blendshapeCount(${blendshapeCount}) が上限 ${LIMITS.blendshapeCount} を超えています。`);
  }
  if (submeshCount === 0 || submeshCount > LIMITS.submeshCount) {
    fail(WHERE, `${label}: submeshCount(${submeshCount}) が 1〜${LIMITS.submeshCount} の範囲外です。`);
  }
  if (indexFormat === 0 && vertexCount > 65535) {
    fail(WHERE, `${label}: indexFormat=uint16 ですが vertexCount(${vertexCount}) が 65535 を超えています。`);
  }

  let off = MESH_BLOCK_HEADER_SIZE;
  const indexBytes = indexFormat === 0 ? 2 : 4;
  const fixedSize =
    MESH_BLOCK_HEADER_SIZE +
    vertexCount * VERTEX_STRIDE +
    submeshCount * SUBMESH_STRIDE +
    totalIndexCount * indexBytes;
  if (!Number.isSafeInteger(fixedSize)) {
    fail(WHERE, `${label}: サイズ計算が安全整数範囲を超えました。`);
  }
  if (blockLength < fixedSize) {
    fail(WHERE, `${label}: ブロック長(${blockLength})が必要サイズ(${fixedSize})に足りません。`);
  }

  // --- Vertices ---
  const positions = new Float32Array(vertexCount * 3);
  const normals = new Float32Array(vertexCount * 3);
  const tangents = new Float32Array(vertexCount * 4);
  const uv0 = new Float32Array(vertexCount * 2);
  for (let i = 0; i < vertexCount; i++) {
    positions[i * 3] = checkFinite(view.getFloat32(off, true), `${label} 頂点${i} position`);
    positions[i * 3 + 1] = checkFinite(view.getFloat32(off + 4, true), `${label} 頂点${i} position`);
    positions[i * 3 + 2] = checkFinite(view.getFloat32(off + 8, true), `${label} 頂点${i} position`);
    normals[i * 3] = checkFinite(view.getFloat32(off + 12, true), `${label} 頂点${i} normal`);
    normals[i * 3 + 1] = checkFinite(view.getFloat32(off + 16, true), `${label} 頂点${i} normal`);
    normals[i * 3 + 2] = checkFinite(view.getFloat32(off + 20, true), `${label} 頂点${i} normal`);
    tangents[i * 4] = checkFinite(view.getFloat32(off + 24, true), `${label} 頂点${i} tangent`);
    tangents[i * 4 + 1] = checkFinite(view.getFloat32(off + 28, true), `${label} 頂点${i} tangent`);
    tangents[i * 4 + 2] = checkFinite(view.getFloat32(off + 32, true), `${label} 頂点${i} tangent`);
    const tw = view.getFloat32(off + 36, true);
    if (!Number.isFinite(tw) || Math.abs(Math.abs(tw) - 1) > 0.001) {
      fail(WHERE, `${label}: 頂点${i} の tangent.w(${tw}) が ±1 ではありません。`);
    }
    tangents[i * 4 + 3] = tw;
    uv0[i * 2] = checkFinite(view.getFloat32(off + 40, true), `${label} 頂点${i} uv`);
    uv0[i * 2 + 1] = checkFinite(view.getFloat32(off + 44, true), `${label} 頂点${i} uv`);
    off += VERTEX_STRIDE;
  }

  // --- SubMeshes ---
  const submeshes: MeshBinSubMesh[] = [];
  let expectedOffset = 0;
  for (let i = 0; i < submeshCount; i++) {
    const indexOffset = view.getUint32(off, true);
    const indexCount = view.getUint32(off + 4, true);
    // indexCount === 0 は許容する（Unity 側に空サブメッシュが存在しうるため）。
    if (indexCount % 3 !== 0) {
      fail(WHERE, `${label}: サブメッシュ${i} の indexCount(${indexCount}) が 3 の倍数ではありません。`);
    }
    if (indexOffset !== expectedOffset) {
      fail(WHERE, `${label}: サブメッシュ${i} の indexOffset(${indexOffset}) が期待値(${expectedOffset})と異なります。`);
    }
    expectedOffset += indexCount;
    submeshes.push({ indexOffset, indexCount });
    off += SUBMESH_STRIDE;
  }
  if (expectedOffset !== totalIndexCount) {
    fail(WHERE, `${label}: サブメッシュの indexCount 合計(${expectedOffset})が totalIndexCount(${totalIndexCount})と一致しません。`);
  }

  // --- Indices ---
  let indices: Uint16Array | Uint32Array;
  if (indexFormat === 0) {
    const arr = new Uint16Array(totalIndexCount);
    for (let i = 0; i < totalIndexCount; i++) {
      arr[i] = view.getUint16(off, true);
      off += 2;
    }
    indices = arr;
  } else {
    const arr = new Uint32Array(totalIndexCount);
    for (let i = 0; i < totalIndexCount; i++) {
      arr[i] = view.getUint32(off, true);
      off += 4;
    }
    indices = arr;
  }
  for (let i = 0; i < totalIndexCount; i++) {
    if (indices[i] >= vertexCount) {
      fail(WHERE, `${label}: インデックス[${i}]=${indices[i]} が vertexCount(${vertexCount}) 以上です。`);
    }
  }

  // --- Blendshapes（スパース） ---
  const blendshapes: MeshBinBlendshape[] = [];
  for (let s = 0; s < blendshapeCount; s++) {
    if (off + 4 > blockLength) {
      fail(WHERE, `${label}: シェイプ${s} の sparseCount がブロック末尾を超えました。`);
    }
    const sparseCount = view.getUint32(off, true);
    off += 4;
    if (sparseCount > vertexCount) {
      fail(WHERE, `${label}: シェイプ${s} の sparseCount(${sparseCount}) が vertexCount(${vertexCount}) を超えています。`);
    }
    if (off + sparseCount * DELTA_STRIDE > blockLength) {
      fail(WHERE, `${label}: シェイプ${s} のデルタ領域がブロック末尾を超えました。`);
    }
    const bsIndices = new Uint32Array(sparseCount);
    const posDeltas = new Float32Array(sparseCount * 3);
    const normDeltas = new Float32Array(sparseCount * 3);
    let prev = -1;
    for (let d = 0; d < sparseCount; d++) {
      const vi = view.getUint32(off, true);
      if (vi >= vertexCount) {
        fail(WHERE, `${label}: シェイプ${s} デルタ${d} の vertexIndex(${vi}) が vertexCount(${vertexCount}) 以上です。`);
      }
      if (vi <= prev) {
        fail(WHERE, `${label}: シェイプ${s} デルタ${d} の vertexIndex(${vi}) が昇順・重複なしではありません（前=${prev}）。`);
      }
      prev = vi;
      bsIndices[d] = vi;
      for (let k = 0; k < 3; k++) {
        posDeltas[d * 3 + k] = checkFinite(view.getFloat32(off + 4 + k * 4, true), `${label} シェイプ${s} posDelta`);
        normDeltas[d * 3 + k] = checkFinite(view.getFloat32(off + 16 + k * 4, true), `${label} シェイプ${s} normDelta`);
      }
      off += DELTA_STRIDE;
    }
    blendshapes.push({ indices: bsIndices, posDeltas, normDeltas });
  }

  if (off > blockLength) {
    fail(WHERE, `${label}: 読み取り位置(${off})がブロック長(${blockLength})を超えました。`);
  }

  return {
    meshIndex,
    vertexCount,
    submeshCount,
    blendshapeCount,
    totalIndexCount,
    indexFormat,
    transform,
    positions,
    normals,
    tangents,
    uv0,
    submeshes,
    indices,
    blendshapes,
  };
}
